import React, { useCallback, useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import createClasses from './styles';
import { useTranslation } from 'react-i18next';
import { putRequest } from '../../../utils/requests';
import {
  Checkbox,
  InputLabel,
  showNotif,
  useRightSideBar
} from '@p81-common/ui';

interface SetPAYGTenantProps {
  companyName: string;
  customerId: string;
  updateDataFn(): void;
}

const SetPAYGTenant = ({
  companyName,
  customerId,
  updateDataFn
}: SetPAYGTenantProps) => {
  const { t } = useTranslation(['activity']);
  const classes = createClasses();
  const { setShowBottomActionBar, setOpenSecondary } = useRightSideBar();
  const [confirmationCheckbox, setConfirmationCheckbox] =
    useState<boolean>(false);

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault();

      try {
        const response = await putRequest('customer/cp/set-payg', {
          customerId
        });

        if (response.statusCode >= 400) {
          throw new Error();
        }

        showNotif(t('activity:setTenantPAYG.SUCCESS', { companyName }), {
          alertProps: {
            severity: 'success',
            variant: 'filled',
            style: { width: 'max-content' }
          }
        });
        updateDataFn();
        setOpenSecondary(false);
      } catch (e) {
        showNotif(t('activity:setTenantPAYG.FAILED'), {
          alertProps: {
            severity: 'error',
            variant: 'filled',
            style: { width: 'max-content' }
          }
        });
        setOpenSecondary(false);
      }
    },
    [companyName, setOpenSecondary, t, customerId, updateDataFn]
  );

  const handleCheckbox = () => {
    setConfirmationCheckbox(prev => !prev);
  };

  useEffect(() => {
    setShowBottomActionBar({
      okButtonProps: {
        text: t('buttons.APPLY'),
        disabled: !confirmationCheckbox,
        type: 'submit',
        onClick: e => handleSubmit(e)
      },
      cancelButtonProps: {
        text: t('buttons.CANCEL'),
        onClick: () => {
          setOpenSecondary(false);
          setShowBottomActionBar(null);
        }
      },
      isSideBar: true
    });
  }, [
    confirmationCheckbox,
    handleSubmit,
    setOpenSecondary,
    setShowBottomActionBar,
    t
  ]);

  return (
    <form className={classes.container} onSubmit={handleSubmit}>
      <Typography
        variant="h2"
        className={classes.heading}
        dangerouslySetInnerHTML={{
          __html: t('setTenantPAYG.TITLE', { companyName })
        }}
      />
      <Typography variant="subtitle1" className={classes.subtitle}>
        {t('setTenantPAYG.SUBTITLE')}
      </Typography>

      <InputLabel className={classes.checkbox}>
        <Checkbox value={confirmationCheckbox} onChange={handleCheckbox} />
        <Typography variant="subtitle2">
          {t('setTenantPAYG.CHECKBOX_LABEL')}
        </Typography>
      </InputLabel>
    </form>
  );
};

export default SetPAYGTenant;
